import { FC, useCallback } from "react";

import { enqueueSnackbar } from "notistack";

import { ButtonCopy } from "../../../../../components/ButtonCopy/ButtonCopy";
import { AutoHideDuration } from "../../../../../constants/Notistack";
import { useTranslate } from "../../../../../hooks/useTranslate";

import { DashboardButton } from "@/dashboard-design-system/Button/DashboardButton";
import {
  FormConfirmationDialog,
  InnerFormConfirmationDialogProps,
} from "@/forms-design-system/FormDialog/FormConfirmationDialog";

export type CampaignIsLiveDialogProps = Pick<InnerFormConfirmationDialogProps, "open" | "onClose" | "inline"> & {
  isTeamCampaign?: boolean;
  isIndividualCampaign?: boolean;
  fundraiserName?: string;
  formLink: string;
};

export const CampaignIsLiveDialog: FC<CampaignIsLiveDialogProps> = ({
  open,
  onClose,
  isTeamCampaign,
  isIndividualCampaign,
  fundraiserName,
  formLink,
  inline,
}) => {
  const { t } = useTranslate();

  const onCopy = useCallback(() => {
    enqueueSnackbar(t("donationForm", "linkCopied"), {
      vibe: "positive",
      hideClose: true,
      autoHideDuration: AutoHideDuration.fast,
    });
  }, [t]);

  const title = isTeamCampaign
    ? t("donationForm", "campaignIsLiveDialog.title.team")
    : isIndividualCampaign
      ? t("donationForm", "campaignIsLiveDialog.title.individual", { fundraiserName })
      : "";

  const description = isTeamCampaign
    ? t("donationForm", "campaignIsLiveDialog.description.team")
    : isIndividualCampaign
      ? t("donationForm", "campaignIsLiveDialog.description.individual")
      : "";

  const actionButtons = [
    <ButtonCopy
      key="copy"
      variant="filled"
      vibe="positive"
      onClick={onCopy}
      toCopy={formLink}
      copyText={t("donationForm", "campaignIsLiveDialog.button.copyShareLink")}
    />,
    <DashboardButton key="view" variant="outlined" vibe="neutral" onClick={onClose}>
      {t("donationForm", "campaignIsLiveDialog.button.viewCampaign")}
    </DashboardButton>,
  ];

  return (
    <FormConfirmationDialog
      title={<span data-test="campaign-is-live-dialog-title">{title}</span>}
      description={description}
      open={open}
      onClose={onClose}
      actions={actionButtons}
      inline={inline}
    />
  );
};
